import React from 'react';
import { Storage, AsyncStorage } from 'services';
import { BootScreen } from 'screens';

// Types
type GroupsSidebarActionType =
  | 'OPEN_SIDEBAR'
  | 'CLOSE_SIDEBAR'
  | 'TOGGLE_SIDEBAR'
  | 'TOGGLE_GROUP_COLLAPSE'
  | 'START_GROUP_SEARCH'
  | 'CANCEL_GROUP_SEARCH'
  | 'CREATE_GROUP';

type GroupsSidebarAction = { type: GroupsSidebarActionType; payload?: boolean | string };

type GroupsSidebarDispatch = (action: GroupsSidebarAction) => void;

interface GroupsSidebarState {
  /** @boolean If the groups sidebar is open */
  open: boolean;
  /** @string[] List of id's of the groups that are collapsed  */
  collapsedGroups: string[];
  /** @string ID of the group that's searching for users to add */
  searchGroupId?: string;
  /** @string ID of the last created group  */
  createdGroupId?: string;
}

const GroupsSidebarStateContext = React.createContext<GroupsSidebarState | undefined>(undefined);
const GroupsSidebarDispatchContext = React.createContext<GroupsSidebarDispatch | undefined>(
  undefined
);

const getCollapsedFromStorage = async (): Promise<string[]> => {
  try {
    const groups = (await AsyncStorage.get('groups')) || [];
    const collapsed = JSON.parse(String(groups));
    return collapsed;
  } catch (err) {
    return [];
  }
};

const getInitialState = async () => {
  const collapsedGroups = await getCollapsedFromStorage();

  return {
    open: false,
    searchGroupId: undefined,
    collapsedGroups: collapsedGroups,
    createdGroupId: '',
  };
};

const sidebarReducer = (state: GroupsSidebarState, action: GroupsSidebarAction) => {
  switch (action.type) {
    case 'OPEN_SIDEBAR':
      return { ...state, open: true };
    case 'CLOSE_SIDEBAR':
      return { ...state, open: false };
    case 'TOGGLE_SIDEBAR':
      return { ...state, open: !state.open };
    case 'TOGGLE_GROUP_COLLAPSE':
      const collapsedGroups = handleCollapseToggle(state.collapsedGroups, action.payload as string);
      Storage.set('groups', JSON.stringify(collapsedGroups));
      return { ...state, collapsedGroups };
    case 'START_GROUP_SEARCH':
      return { ...state, searchGroupId: action.payload as string };
    case 'CANCEL_GROUP_SEARCH':
      return { ...state, searchGroupId: undefined };
    case 'CREATE_GROUP':
      return { ...state, createdGroupId: action.payload as string };
    default:
      return state;
  }
};

const handleCollapseToggle = (groupIds: string[], collapsedId: string): string[] => {
  if (groupIds.includes(collapsedId)) {
    return groupIds.filter((id) => id !== collapsedId);
  } else {
    return [...groupIds, collapsedId];
  }
};

export const GroupsSidebarProvider: React.FC = ({ children }: any) => {
  const [state, setState] = React.useState<GroupsSidebarState>();

  React.useEffect(() => {
    getInitialState().then((initialState) => {
      setState(initialState);
    });
  }, []);

  if (!state) return <BootScreen />;

  return <Provider initialState={state}>{children}</Provider>;
};

const Provider: React.FC<{ children: React.ReactNode; initialState: unknown }> = ({
  children,
  initialState,
}: any) => {
  const [state, dispatch] = React.useReducer(sidebarReducer, initialState);

  return (
    <GroupsSidebarStateContext.Provider value={state}>
      <GroupsSidebarDispatchContext.Provider value={dispatch}>
        {children}
      </GroupsSidebarDispatchContext.Provider>
    </GroupsSidebarStateContext.Provider>
  );
};

export const useGroupsSidebarState = () => {
  const context = React.useContext(GroupsSidebarStateContext);
  if (context === undefined) {
    throw new Error('useGroupsSidebarState must be used within a GroupsSidebarProvider');
  }
  return context;
};

export const useGroupsSidebarDispatch = () => {
  const context = React.useContext(GroupsSidebarDispatchContext);
  if (context === undefined) {
    throw new Error('useGroupsSidebarDispatch must be used within a GroupsSidebarProvider');
  }
  return context;
};
